import { useRef, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { DrinkLogSection } from '@/components/sections/DrinkLogSection';
import { RewardsSection } from '@/components/sections/RewardsSection';
import { LeaderboardSection } from '@/components/sections/LeaderboardSection';
import { ProfileSection } from '@/components/sections/ProfileSection';
import { OnePageNav } from '@/components/OnePageNav';
import { OnePageHeader } from '@/components/OnePageHeader';
import { IntroScreen } from '@/components/IntroScreen';

export type SectionId = 'drink-log' | 'rewards' | 'leaderboard' | 'profile';

export default function OnePager() {
  const { user, profile, membership, loading, group } = useAuth();
  
  const [activeSection, setActiveSection] = useState<SectionId>('drink-log');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const drinkLogRef = useRef<HTMLElement>(null);
  const rewardsRef = useRef<HTMLElement>(null);
  const leaderboardRef = useRef<HTMLElement>(null);
  const profileRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const sectionRefs = {
    'drink-log': drinkLogRef,
    'rewards': rewardsRef,
    'leaderboard': leaderboardRef,
    'profile': profileRef,
  };

  // Scroll to section
  const scrollToSection = useCallback((sectionId: SectionId) => {
    const ref = sectionRefs[sectionId];
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Close menu after navigation
    setIsMenuOpen(false);
  }, []);

  // Track active section based on scroll position
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const sections: SectionId[] = ['drink-log', 'rewards', 'leaderboard', 'profile'];
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const ref = sectionRefs[section];
        if (ref.current) {
          const offsetTop = ref.current.offsetTop - container.offsetTop;
          if (scrollTop >= offsetTop - 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle modal open/close for scroll locking
  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
    setIsMenuOpen(false); // Close hamburger when modal opens
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Show loading screen while resolving state
  if (loading) {
    return <IntroScreen />;
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // User needs to complete onboarding
  if (!profile?.user_id || !membership) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen-safe bg-background flex flex-col">
      {/* Header */}
      <OnePageHeader 
        activeSection={activeSection}
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        onNavigate={scrollToSection}
      />

      {/* Scrollable Content */}
      <div 
        ref={containerRef}
        className={`flex-1 overflow-y-auto ${isModalOpen ? 'overflow-hidden' : ''}`}
        style={{ scrollBehavior: 'smooth' }}
      >
        <DrinkLogSection 
          ref={drinkLogRef}
          onModalOpen={handleModalOpen}
          onModalClose={handleModalClose}
        />
        
        <RewardsSection 
          ref={rewardsRef}
          onModalOpen={handleModalOpen}
          onModalClose={handleModalClose}
        />
        
        <LeaderboardSection 
          ref={leaderboardRef}
          onModalOpen={handleModalOpen}
          onModalClose={handleModalClose}
        />
        
        <ProfileSection 
          ref={profileRef}
          onModalOpen={handleModalOpen}
          onModalClose={handleModalClose}
        />
        
        {/* Bottom padding for nav */}
        <div className="h-[68px]" />
      </div>

      {/* Bottom Navigation */}
      <OnePageNav 
        activeSection={activeSection}
        onNavigate={scrollToSection}
        onNavClick={() => setIsMenuOpen(false)}
      />
    </div>
  );
}
